import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Tab,
  Tabs,
  Container,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Typography,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2"
import { Navigate, useNavigate } from "react-router-dom";

import ConfigureProgram from "./ConfigureProgram";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { useState } from "react";
import CreateProgramDialog from "./CreateProgramDialog";
import AddRemoveList from "~/components/common/AddRemoveList";
import { EligibilityQuestionAnswerFormatTypeEnum as AnswerFormatType, useDeleteProgramEligibilityQuestionMutation } from '~/graphql/__generated__/graphql';
import { gql } from '@apollo/client';
import CreateQuestionDialog from './CreateQuestionDialog';
import { answerFormats } from './QuestionForm';
import EligibilityQuestionFormRow from './EligibilityQuestionFormRow';

interface TabProgramProps {
  id?: number;
}

const TabPrograms = ({ id }: TabProgramProps) => {
  const { eligibilityQuestions, programs } = useOrgConfig();
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createQuestion, setCreateQuestion] = useState<AnswerFormatType | false>(false);
  const [deleteEligibilityQuestion, { loading: loadingDelete }] = useDeleteProgramEligibilityQuestionMutation();

  const tab = id;
  if (!tab) {
    const programId = Object.values(programs)[0]?.id;
    if (programId) {
      return <Navigate to={`/predict-config/programs/${programId}`} replace />;
    }
  }

  const questions = tab ? Object.values(eligibilityQuestions).flatMap(q => {
    if (!q) { return []; }

    const appliesToCurrentProgram = q.programEligibilityQuestions.some(link => link.programId === tab);
    const appliesToNoPrograms = q.programEligibilityQuestions.length === 0;

    if (!appliesToCurrentProgram && !appliesToNoPrograms) { return []; }

    return [{
      key: q.id,
      label: <Accordion sx={{ width: "100%", mr: 2 , "&.Mui-expanded": { mr: 2 }}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {q.question}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <EligibilityQuestionFormRow key={q.id} question={q} />
        </AccordionDetails>
      </Accordion>,
    }];
  }) : undefined;

  const onAdd = ([index]: number[]) => {
    if (!answerFormats[index]) { return }
    setCreateQuestion(answerFormats[index].format as AnswerFormatType)
  }

  const onDelete = (eligibilityQuestionId: number) => deleteEligibilityQuestion({
    variables: { eligibilityQuestionId },
    awaitRefetchQueries: true,
    refetchQueries: ["OrgConfig"],
  });

  return <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4, lg: 3, xl: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => { navigate(`/predict-config/programs/${newValue}`); }}
          orientation="vertical"
          variant="scrollable"
          sx={{ maxHeight: 400 }}
          visibleScrollbar={true}
        >
          {Object.values(programs).map((p) => p && <Tab key={p.id} label={p.name} value={p.id} />)}
        </Tabs>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
          sx={{ mt: 2, width: "100%" }}
        >
          Create Program
        </Button>

        {createDialogOpen && <CreateProgramDialog onClose={() => setCreateDialogOpen(false)} />}
      </Grid>

      <Grid size={{ xs: 12, md: 8, lg: 9, xl: 10 }}>
        {tab && programs[tab] && <Grid container key={tab} spacing={2} sx={{ alignItems: "stretch" }}>
          <Grid size={{ xs: 12, xl: 6 }}>
            <ConfigureProgram program={programs[tab]} />
          </Grid>
          <Grid size={{ xs: 12, xl: 6 }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography>Eligibility Questions</Typography>
                <Divider sx={{ mt: 1 }} />
                <AddRemoveList
                  emptyLabel="No Eligibility Questions configured"
                  items={questions ?? []}
                  addOptions={answerFormats}
                  addOnSelect={true}

                  addLabel="Add question"
                  onAdd={onAdd}
                  addLoading={false}

                  onRemove={onDelete}
                  removeLoading={loadingDelete}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>}
      </Grid>
    </Grid>

    {createQuestion && tab && <CreateQuestionDialog
      programId={tab}
      answerFormatType={createQuestion}
      onClose={() => setCreateQuestion(false)}
    />}
  </Container>
}

export default TabPrograms;

gql(`
  mutation DeleteProgramEligibilityQuestion(
    $eligibilityQuestionId: Int!
  )
  @permission(requires: manageOrgConfig)
  {
    deleteProgramEligibilityQuestion(
      id: $eligibilityQuestionId
    ) {
      id
      deleted
    }
  }
`);
